import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-galvani-bg" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass py-4 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-galvani-text hover:text-galvani-purple transition-colors" data-testid="back-home-link">
            <ArrowLeft size={18} />
            <span className="font-body text-sm">Volver al inicio</span>
          </a>
          <span className="font-heading font-bold text-galvani-text">Galvani Studios</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-galvani-text mb-4">
          Política de <span className="gradient-text">Privacidad</span>
        </h1>
        <p className="font-body text-galvani-textLight mb-12">
          Última actualización: {new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}
        </p>

        <div className="prose prose-slate max-w-none space-y-8 font-body text-galvani-text leading-relaxed">

          <section>
            <h2 className="font-heading font-bold text-2xl text-galvani-text mb-4">1. Responsable del tratamiento</h2>
            <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <ul className="space-y-2 text-sm">
                <li><strong>Razón social:</strong> GALVANI STUDIOS SL</li>
                <li><strong>N.I.F.:</strong> B42810549</li>
                <li><strong>Domicilio:</strong> C/ Fernando el Católico 57, local — 28015 Madrid (España)</li>
                <li><strong>Email de contacto:</strong> <a href="mailto:info@galvanistudios.com" className="text-galvani-blue hover:underline">info@galvanistudios.com</a></li>
                <li><strong>Teléfono:</strong> +34 683 39 78 73</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="font-heading font-bold text-2xl text-galvani-text mb-4">2. Información que recopilamos</h2>
            <p className="mb-3">En Galvani Studios SL recogemos los siguientes datos personales cuando interactúas con nuestra web:</p>
            <ul className="list-disc pl-6 space-y-2 text-galvani-textLight">
              <li><strong>Datos identificativos:</strong> nombre y apellidos.</li>
              <li><strong>Datos de contacto:</strong> dirección de correo electrónico, teléfono (opcional).</li>
              <li><strong>Datos profesionales:</strong> empresa, cargo, objetivos de negocio (cuando los facilites en formularios de pre-cualificación).</li>
              <li><strong>Datos de navegación:</strong> dirección IP, tipo de dispositivo, páginas visitadas (a través de cookies).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-2xl text-galvani-text mb-4">3. Finalidad del tratamiento</h2>
            <p className="mb-3">Tratamos tus datos con las siguientes finalidades:</p>
            <ul className="list-disc pl-6 space-y-2 text-galvani-textLight">
              <li>Envío de la guía gratuita en PDF solicitada (lead magnet).</li>
              <li>Gestión de tu solicitud de información o presupuesto.</li>
              <li>Envío de comunicaciones comerciales relacionadas con servicios de producción de videopodcasts (siempre que hayas dado tu consentimiento).</li>
              <li>Análisis estadístico y mejora de nuestra web.</li>
              <li>Cumplimiento de obligaciones legales.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-2xl text-galvani-text mb-4">4. Base legal</h2>
            <p>La base legal para el tratamiento de tus datos es tu <strong>consentimiento expreso</strong>, que prestas al marcar la casilla correspondiente en nuestros formularios, y la <strong>ejecución de un acuerdo precontractual</strong> cuando solicitas información sobre nuestros servicios.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-2xl text-galvani-text mb-4">5. Conservación de los datos</h2>
            <p>Conservaremos tus datos mientras se mantenga la relación comercial o durante los plazos legalmente establecidos. Podrás solicitar su supresión en cualquier momento escribiendo a <a href="mailto:info@galvanistudios.com" className="text-galvani-blue hover:underline">info@galvanistudios.com</a>.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-2xl text-galvani-text mb-4">6. Destinatarios y cesiones</h2>
            <p className="mb-3">Tus datos podrán ser comunicados a los siguientes encargados de tratamiento, únicamente para las finalidades indicadas:</p>
            <ul className="list-disc pl-6 space-y-2 text-galvani-textLight">
              <li><strong>MailerLite</strong> (plataforma de email marketing): para el envío automatizado de la guía y comunicaciones.</li>
              <li><strong>Google LLC</strong> (Google Analytics y Google Tag Manager): para análisis estadístico.</li>
              <li><strong>Proveedores de hosting</strong> ubicados en la Unión Europea.</li>
            </ul>
            <p className="mt-3">No realizaremos cesiones de tus datos a terceros salvo obligación legal.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-2xl text-galvani-text mb-4">7. Tus derechos</h2>
            <p className="mb-3">En cumplimiento del RGPD y la LOPDGDD, puedes ejercer en cualquier momento los siguientes derechos:</p>
            <ul className="list-disc pl-6 space-y-2 text-galvani-textLight">
              <li>Acceso a tus datos personales.</li>
              <li>Rectificación de datos inexactos.</li>
              <li>Supresión (derecho al olvido).</li>
              <li>Limitación del tratamiento.</li>
              <li>Portabilidad de los datos.</li>
              <li>Oposición al tratamiento.</li>
              <li>Retirada del consentimiento prestado.</li>
            </ul>
            <p className="mt-3">Para ejercerlos, envía un email a <a href="mailto:info@galvanistudios.com" className="text-galvani-blue hover:underline">info@galvanistudios.com</a> indicando el derecho que deseas ejercitar y adjuntando copia de tu DNI o documento equivalente.</p>
            <p className="mt-3">Asimismo, tienes derecho a presentar una reclamación ante la <strong>Agencia Española de Protección de Datos</strong> (<a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-galvani-blue hover:underline">www.aepd.es</a>) si consideras que el tratamiento no se ajusta a la normativa.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-2xl text-galvani-text mb-4">8. Seguridad</h2>
            <p>Galvani Studios SL aplica las medidas técnicas y organizativas adecuadas para garantizar la seguridad e integridad de los datos personales y evitar su pérdida, alteración o acceso no autorizado.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-2xl text-galvani-text mb-4">9. Modificaciones</h2>
            <p>Nos reservamos el derecho a modificar esta política de privacidad para adaptarla a novedades legislativas o jurisprudenciales. Te recomendamos revisarla periódicamente.</p>
          </section>

        </div>

        <div className="mt-16 p-6 rounded-2xl bg-gradient-to-r from-blue-50 to-purple-50 border border-purple-200 text-center">
          <p className="font-body text-galvani-text mb-2">¿Tienes dudas sobre cómo tratamos tus datos?</p>
          <a href="mailto:info@galvanistudios.com" className="font-heading font-semibold text-galvani-purple hover:underline">
            Contáctanos en info@galvanistudios.com
          </a>
        </div>
      </main>
    </div>
  );
}
